const MAX_AND_EXPRESSIONS = 8;
const MAX_OR_EXPRESSIONS = 3;
const SUPPORTED_LOGIC = new Set(['AND', 'OR']);

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const addError = (errors, code, message, path) => {
  errors.push({ code, message, path });
};

const getSupportedOperators = (attribute) => {
  const operators = attribute?.allowedOperators;
  if (Array.isArray(operators)) return operators.length > 0 ? operators : ['EQUALS'];
  if (operators && typeof operators === 'object') {
    const supportedOperators = Object.entries(operators)
      .filter(([, supported]) => supported)
      .map(([operator]) => operator);
    return supportedOperators.length > 0 ? supportedOperators : ['EQUALS'];
  }
  return ['EQUALS'];
};

const validateCondition = (condition, path, attributesById, errors) => {
  if (!condition || typeof condition !== 'object' || Array.isArray(condition)) {
    addError(errors, 'INVALID_CONDITION', 'Expression condition must be an object.', path);
    return;
  }

  const attribute = attributesById.get(condition.attributeId);
  if (!attribute) {
    addError(errors, 'ATTRIBUTE_NOT_FOUND', `Attribute '${condition.attributeId || ''}' does not exist.`, `${path}.attributeId`);
    return;
  }

  if (!hasValue(condition.value)) {
    addError(errors, 'ATTRIBUTE_VALUE_REQUIRED', `Attribute '${condition.attributeId}' must have a value.`, `${path}.value`);
  } else if (Array.isArray(attribute.allowedValues) && attribute.allowedValues.length > 0) {
    const allowedValues = attribute.allowedValues.map((item) => (
      item && typeof item === 'object' ? item.value : item
    ));
    if (!allowedValues.includes(condition.value)) {
      addError(errors, 'ATTRIBUTE_VALUE_UNSUPPORTED', `Value '${condition.value}' is not supported by attribute '${condition.attributeId}'.`, `${path}.value`);
    }
  }

  if (!getSupportedOperators(attribute).includes(condition.operator)) {
    addError(errors, 'OPERATOR_UNSUPPORTED', `Operator '${condition.operator || ''}' is not supported by attribute '${condition.attributeId}'.`, `${path}.operator`);
  }

  if (!hasValue(condition.proficiency)) {
    addError(errors, 'PROFICIENCY_REQUIRED', `Attribute '${condition.attributeId}' must have a proficiency.`, `${path}.proficiency`);
  } else if (!Number.isInteger(Number(condition.proficiency)) || Number(condition.proficiency) < 1 || Number(condition.proficiency) > 5) {
    addError(errors, 'PROFICIENCY_OUT_OF_RANGE', 'Proficiency must be an integer from 1 through 5.', `${path}.proficiency`);
  }
};

const validateAndExpression = (expression, path, attributesById, errors) => {
  if (!expression || expression.logic !== 'AND' || !Array.isArray(expression.conditions) || expression.conditions.length === 0) {
    addError(errors, 'INVALID_AND_EXPRESSION', 'Each OR branch must be a non-empty AND expression.', path);
    return;
  }
  if (expression.conditions.length > MAX_AND_EXPRESSIONS) {
    addError(errors, 'TOO_MANY_AND_EXPRESSIONS', `An AND expression supports at most ${MAX_AND_EXPRESSIONS} conditions.`, `${path}.conditions`);
  }
  expression.conditions.forEach((condition, index) => {
    const conditionPath = `${path}.conditions[${index}]`;
    if (Array.isArray(condition?.conditions)) {
      addError(errors, condition.logic === 'OR' ? 'OR_NOT_TOP_LEVEL' : 'INVALID_EXPRESSION_STRUCTURE', 'Nested expression groups are not supported.', conditionPath);
      return;
    }
    validateCondition(condition, conditionPath, attributesById, errors);
  });
};

const validateCriteria = (criteria, path, attributesById, errors) => {
  if (!criteria || !SUPPORTED_LOGIC.has(criteria.logic) || !Array.isArray(criteria.conditions) || criteria.conditions.length === 0) {
    addError(errors, 'INVALID_EXPRESSION_STRUCTURE', 'Criteria must be a non-empty AND or OR expression.', path);
    return;
  }

  if (criteria.logic === 'OR') {
    if (criteria.conditions.length > MAX_OR_EXPRESSIONS) {
      addError(errors, 'TOO_MANY_OR_EXPRESSIONS', `Criteria supports at most ${MAX_OR_EXPRESSIONS} top-level OR expressions.`, `${path}.conditions`);
    }
    criteria.conditions.forEach((expression, index) => {
      const expressionPath = `${path}.conditions[${index}]`;
      if (Array.isArray(expression?.conditions)) {
        validateAndExpression(expression, expressionPath, attributesById, errors);
      } else {
        validateCondition(expression, expressionPath, attributesById, errors);
      }
    });
    return;
  }

  validateAndExpression(criteria, path, attributesById, errors);
};

export const validateRoutingPolicy = (policy, routingGroups = [], attributes = []) => {
  const errors = [];
  const steps = Array.isArray(policy?.routingSteps) ? policy.routingSteps : [];
  const groupsById = new Map(routingGroups.map((group) => [group.routingGroupId, group]));
  const attributesById = new Map(attributes.map((attribute) => [attribute.attributeId, attribute]));

  if (steps.length === 0) {
    addError(errors, 'POLICY_STEP_REQUIRED', 'Policy must have at least one routing step.', 'routingSteps');
    return { valid: false, errors };
  }

  const stepNumbers = steps.map((step) => Number(step.step));
  if (new Set(stepNumbers).size !== stepNumbers.length) {
    addError(errors, 'DUPLICATE_STEP_NUMBER', 'Routing step numbers must be unique.', 'routingSteps');
  }
  if (stepNumbers.some((stepNumber, index) => !Number.isInteger(stepNumber) || stepNumber !== index + 1)) {
    addError(errors, 'NON_SEQUENTIAL_STEPS', 'Routing steps must be sequential and ordered starting at 1.', 'routingSteps');
  }

  steps.forEach((step, index) => {
    const stepPath = `routingSteps[${index}]`;
    const group = groupsById.get(step.routingGroupId);
    if (!group) {
      addError(errors, 'ROUTING_GROUP_NOT_FOUND', `Routing group '${step.routingGroupId || ''}' does not exist.`, `${stepPath}.routingGroupId`);
    } else {
      validateCriteria(group.criteria, `${stepPath}.criteria`, attributesById, errors);
    }

    if (index < steps.length - 1 && !hasValue(step.timeoutSeconds)) {
      addError(errors, 'STEP_EXPIRATION_REQUIRED', 'Every routing step before the final step must have an expiration.', `${stepPath}.timeoutSeconds`);
    } else if (hasValue(step.timeoutSeconds) && (!Number.isInteger(Number(step.timeoutSeconds)) || Number(step.timeoutSeconds) <= 0)) {
      addError(errors, 'INVALID_STEP_EXPIRATION', 'Step expiration must be a positive integer number of seconds.', `${stepPath}.timeoutSeconds`);
    }
  });

  return { valid: errors.length === 0, errors };
};

export default validateRoutingPolicy;